/**
 * Pricing Settings - использует переиспользуемые компоненты настроек
 */
import React from 'react';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import {
  SettingsGroup,
  TextSettings,
  SelectFieldSettings,
} from '../components/settings';

// Хелпер для работы с текстовыми данными
const getTextData = (value) => {
  if (typeof value === 'object' && value !== null) return value;
  return { text: value || '' };
};

export default function PricingSettings({ data, onChange }) {
  const updateData = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const updatePlan = (index, field, value) => {
    const newPlans = [...(data?.plans || [])];
    newPlans[index] = { ...newPlans[index], [field]: value };
    updateData('plans', newPlans);
  };

  const updatePlanFeature = (planIndex, featureIndex, value) => {
    const newPlans = [...(data?.plans || [])];
    const newFeatures = [...(newPlans[planIndex].features || [])];
    newFeatures[featureIndex] = value;
    newPlans[planIndex] = { ...newPlans[planIndex], features: newFeatures };
    updateData('plans', newPlans);
  };

  const addFeature = (planIndex) => {
    const newPlans = [...(data?.plans || [])];
    const newFeatures = [...(newPlans[planIndex].features || []), 'Новая функция'];
    newPlans[planIndex] = { ...newPlans[planIndex], features: newFeatures };
    updateData('plans', newPlans);
  };

  const removeFeature = (planIndex, featureIndex) => {
    const newPlans = [...(data?.plans || [])];
    const newFeatures = (newPlans[planIndex].features || []).filter((_, i) => i !== featureIndex);
    newPlans[planIndex] = { ...newPlans[planIndex], features: newFeatures };
    updateData('plans', newPlans);
  };

  const addPlan = () => {
    const newPlans = [
      ...(data?.plans || []),
      {
        id: Date.now().toString(),
        name: 'Новый тариф',
        price: '0',
        period: 'час',
        description: '',
        features: ['Функция 1'],
        highlighted: false,
        buttonText: 'Выбрать'
      }
    ];
    updateData('plans', newPlans);
  };

  const removePlan = (index) => {
    const newPlans = (data?.plans || []).filter((_, i) => i !== index);
    updateData('plans', newPlans);
  };

  return (
    <div className="space-y-6">
      {/* Контент */}
      <SettingsGroup title="Контент">
        <TextSettings
          label="Заголовок"
          value={getTextData(data?.title)}
          onChange={(value) => updateData('title', value)}
          placeholder="Тарифы"
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
        />
        <TextSettings
          label="Подзаголовок"
          value={getTextData(data?.subtitle)}
          onChange={(value) => updateData('subtitle', value)}
          placeholder="Выберите подходящий"
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
        />
      </SettingsGroup>

      {/* Настройки */}
      <SettingsGroup title="Настройки">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-secondary">Валюта</label>
            <Input
              value={data?.currency || '₽'}
              onChange={(e) => updateData('currency', e.target.value)}
              placeholder="₽"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 text-text-secondary">Период</label>
            <Input
              value={data?.period || 'месяц'}
              onChange={(e) => updateData('period', e.target.value)}
              placeholder="месяц"
            />
          </div>
        </div>

        <SelectFieldSettings
          label="Стиль"
          value={data?.style || 'cards'}
          onChange={(value) => updateData('style', value)}
          mode="grid"
          columns={3}
          options={[
            { value: 'cards', label: 'Карточки', icon: 'LayoutGrid' },
            { value: 'compact', label: 'Компакт', icon: 'List' },
            { value: 'highlighted', label: 'Слитный', icon: 'Layers' },
          ]}
        />

        {/* Переключатель месяц/год */}
        <div className="pt-2 border-t border-border">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data?.showPeriodToggle || false}
              onChange={(e) => updateData('showPeriodToggle', e.target.checked)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm">Переключатель месяц/год</span>
          </label>

          {data?.showPeriodToggle && (
            <div className="mt-3 space-y-3 pl-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-secondary">Подпись "месяц"</label>
                  <Input
                    value={data?.monthlyLabel || 'Месяц'}
                    onChange={(e) => updateData('monthlyLabel', e.target.value)}
                    placeholder="Месяц"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-text-secondary">Подпись "год"</label>
                  <Input
                    value={data?.yearlyLabel || 'Год'}
                    onChange={(e) => updateData('yearlyLabel', e.target.value)}
                    placeholder="Год"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 text-text-secondary">Бейдж скидки</label>
                <Input
                  value={data?.yearlyDiscount || ''}
                  onChange={(e) => updateData('yearlyDiscount', e.target.value)}
                  placeholder="-20%"
                />
              </div>
            </div>
          )}
        </div>
      </SettingsGroup>

      {/* Тарифы */}
      <SettingsGroup
        title={`Тарифы (${(data?.plans || []).length})`}
        rightAction={
          <button
            onClick={addPlan}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Icon name="Plus" size={12} />
            <span>Добавить</span>
          </button>
        }
      >
        <div className="space-y-4">
          {(data?.plans || []).map((plan, index) => (
            <div key={plan.id || index} className="p-3 bg-muted rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-secondary">{plan.name || `Тариф ${index + 1}`}</span>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={plan.highlighted || false}
                      onChange={(e) => updatePlan(index, 'highlighted', e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-border text-primary focus:ring-primary"
                    />
                    <span>Выделить</span>
                  </label>
                  <button
                    onClick={() => removePlan(index)}
                    className="p-1 hover:bg-error/10 hover:text-error rounded"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={plan.name || ''}
                  onChange={(e) => updatePlan(index, 'name', e.target.value)}
                  placeholder="Название"
                />
                <Input
                  value={plan.period || ''}
                  onChange={(e) => updatePlan(index, 'period', e.target.value)}
                  placeholder="Период (месяц)"
                />
              </div>

              <div className={`grid gap-2 ${data?.showPeriodToggle ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    {data?.showPeriodToggle ? 'Цена (месяц)' : 'Цена'}
                  </label>
                  <Input
                    value={plan.price || ''}
                    onChange={(e) => updatePlan(index, 'price', e.target.value)}
                    placeholder="1000"
                  />
                </div>
                {data?.showPeriodToggle && (
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Цена (год/мес)</label>
                    <Input
                      value={plan.yearlyPrice || ''}
                      onChange={(e) => updatePlan(index, 'yearlyPrice', e.target.value)}
                      placeholder="800"
                    />
                  </div>
                )}
              </div>

              <Input
                value={plan.description || ''}
                onChange={(e) => updatePlan(index, 'description', e.target.value)}
                placeholder="Описание тарифа"
              />

              {/* Функции */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-text-secondary">Функции</label>
                  <button
                    onClick={() => addFeature(index)}
                    className="text-xs text-primary hover:text-primary/80"
                  >
                    + Добавить
                  </button>
                </div>
                {(plan.features || []).map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-center gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updatePlanFeature(index, fIndex, e.target.value)}
                      placeholder="Функция"
                      className="flex-1"
                    />
                    <button
                      onClick={() => removeFeature(index, fIndex)}
                      className="p-1.5 text-text-secondary hover:text-error"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={plan.buttonText || ''}
                  onChange={(e) => updatePlan(index, 'buttonText', e.target.value)}
                  placeholder="Текст кнопки"
                />
                <Input
                  value={plan.buttonLink || ''}
                  onChange={(e) => updatePlan(index, 'buttonLink', e.target.value)}
                  placeholder="Ссылка (#, URL)"
                />
              </div>
            </div>
          ))}

          {(data?.plans || []).length === 0 && (
            <p className="text-sm text-text-secondary text-center py-4">
              Нет тарифов. Нажмите "Добавить" чтобы создать.
            </p>
          )}
        </div>
      </SettingsGroup>
    </div>
  );
}
